/**
 * The volumes an Ember humanoid is drawn from, hung on a solved skeleton.
 *
 * Clothing is geometry, not a painted layer: a garment re-uses the body's own volumes, slightly
 * inflated, in its own material. The raster keeps whichever surface is nearest the camera, so a
 * sleeve covers the arm, the arm passes in front of the shirt, and a cape falls behind the back,
 * frame by frame and facing by facing, with no layer order to get wrong. A new pose costs nothing
 * per garment.
 *
 * This file is the vocabulary everything is made of -- the primitive shapes, light and paint -- and
 * the engine that runs it. The body itself is data, like what it wears: content's
 * sprites/ember/bodies.json (flesh, bones, a beast's head, wounds, and the body's own volumes every
 * garment inflates), hair.json (hair), equipment.json (what it wears and holds), run here as models
 * (model.ts) by buildPrimitives and dressed().
 */
import { BONES, DYES, Material, SKIN, dye, hairDye, metalFrom, rampFrom } from "./palette"
import { DEFAULT_BODY_MODEL, EmberModelSlot, EmberSlot, OverBody, emberDesign, emberDesigns, emberHairStyle, emberHairStyles, onEmberModelsChanged, runEmberModel, setModelBuiltins } from "./model"
import type { Facing } from "./raster"
import { DEFAULT_RASTER, FACING_YAW } from "./raster"
import { LimbJoints, Proportions, SIDES, Side, Skeleton, farSide, legLength, sideSign } from "./rig"
import { IDENTITY, Mat3, Vec3, add, apply, cross, deg, distance, dot, length as vecLength, lerp, mul, normalize, rotX, rotY, rotZ, scale, sub, transpose } from "./vec"
import "./content"

/** Which body part a pixel belongs to. Edges between parts are drawn as creases. */
/** `skirt`: one garment round both legs, shaded as torso cloth but outlined against the legs. */
export type PartGroup = "head" | "hair" | "torso" | "bust" | "shoulder" | "armL" | "armR" | "legL" | "legR" | "held" | "skirt" | "tail"

interface PrimitiveBase {
    material: Material
    group: PartGroup
    /**
     * Which segment of its limb this is. Where two segments of one limb meet -- elbow, wrist, knee,
     * ankle -- a line is drawn, as LPC draws every joint; within a segment, none.
     */
    part?: string
    /** Optional colour painted onto the surface, from the hit point in the primitive's own frame. */
    decal?: (local: Vec3) => number | undefined
}

export interface Capsule extends PrimitiveBase {
    kind: "capsule"
    a: Vec3
    b: Vec3
    r: number
}

export interface Ellipsoid extends PrimitiveBase {
    kind: "ellipsoid"
    center: Vec3
    /** Columns are the ellipsoid's local x, y, z axes. */
    basis: Mat3
    radii: Vec3
    /**
     * Squareness: 2 is an ellipsoid; higher flattens toward a box with rounded edges
     * (|x/a|^ex + |y/b|^ey + |z/c|^ez = 1), one number for all axes or one per axis. LPC heads and
     * chests are flat-sided from the front but round in profile: square across, round front to back.
     */
    exponent?: number | Vec3
    /** Keep only the parts of the shell for which this holds (local, unscaled coordinates). */
    keep?: (local: Vec3) => boolean
}

export type Primitive = Capsule | Ellipsoid

export type Sleeves = "none" | "short" | "long"
/** Ember's own hair styles; a look may also name any of the game's (LPC's) styles (HAIR_DESIGNS). */
export type HairStyle = "bald" | "crop" | "short" | "long" | "ponytail" | string

/** What an Ember humanoid wears and looks like. Data, so a new character is a new record. */
export interface EmberLook {
    /** The body model it is drawn with (content's bodies.json): `human` unless named. */
    body?: string
    skin: keyof typeof SKIN | string
    /** A skeleton: bones where the flesh would be (bonesOf), a skull without eyes or hair. */
    bones?: boolean
    /** Its bone's colour, a BONES name (`green`): the game's skeleton body colour. Ivory unless named. */
    boneColor?: string
    /** Torn flesh: open wounds painted over the body (woundsOf), as LPC's zombie has. */
    wounds?: boolean
    /**
     * A beast on the man's rig: `wolf`, LPC's wolfman -- a wolf's head (wolfHeadOf), a ruff of fur at
     * the neck and a tail, fur for skin (palette `wolf-<colour>`), and no hair.
     */
    beast?: "wolf"
    /** An EYE_STYLES name (face.ts); Ember's own by default. */
    eyes?: string
    hair?: { style: HairStyle; color: string }
    /**
     * A shirt: its sleeves, `tunic` over the hips (with `hem: "thigh"` a skirt to mid-thigh), and
     * what shirtOf adds -- a neckline, puffed shoulders, a pattern.
     */
    shirt?: {
        dye: string
        /** A shirt model other than the game's `shirt` (model.ts), which the rest are parameters of. */
        model?: string
        sleeves: Sleeves
        tunic?: boolean
        hem?: "thigh"
        /** `scoop` a round neck, `low` a deep one with the shoulders bare but for straps, `laced` a laced V. */
        neck?: "scoop" | "low" | "laced"
        puffed?: boolean
        pattern?: "stripes" | "lacing"
    }
    /**
     * Leg wear, `trousers` unless its `cut` says otherwise: `shorts` to the knee, `pantaloons` loose
     * and gathered below the calf (pantaloonsOf), a `skirt` round both legs (skirtOf), a legionary's
     * strapped `legion` skirt (legionOf), steel `greaves` (greavesOf; `dye` names a metal).
     */
    pants?: { dye: string; cut?: PantsCut; model?: string }
    /** Footwear, `shoes` unless its `cut` says otherwise (footwearOf); sabatons name a metal. */
    shoes?: { dye: string; cut?: ShoeCut; model?: string }
    belt?: { dye: string; model?: string }
    /** Body armour over the shirt (armorOf): its kind and colour. Equipment, like `held`. */
    armor?: { kind: ArmorKind; color: string; model?: string }
    /**
     * The game's bauldron (bauldronOf): a leather pauldron on the right shoulder, strapped across the
     * chest to the left side, over whatever is worn there. Equipment, like `held`.
     */
    bauldron?: { color: string; model?: string }
    /**
     * A shield (shieldOf): the game's shield item type and colour, in the left fist, or `slung` on
     * the back while that hand has a bow. Equipment, like `held`.
     */
    shield?: { type: string; color: string; slung?: boolean }
    /** What the right hand holds: an item type with a `held` design (`axe`). Equipment, not a fixed part of the look. */
    held?: string
    /** The held item's colour variant, as the item names it (`metal`, `brown`). */
    heldColor?: string
}

const capsule = (a: Vec3, b: Vec3, r: number, material: Material, group: PartGroup): Capsule => ({
    kind: "capsule",
    a,
    b,
    r,
    material,
    group,
})

const ellipsoid = (
    center: Vec3,
    basis: Mat3,
    radii: Vec3,
    material: Material,
    group: PartGroup,
    extra: Partial<Ellipsoid> = {},
): Ellipsoid => ({ kind: "ellipsoid", center, basis, radii, material, group, ...extra })

const inflate = (r: Vec3, by: number): Vec3 => [r[0] + by, r[1] + by, r[2] + by]

/**
 * A limb segment shaped like muscle rather than pipe: an ellipsoid along the bone, fullest in the
 * middle and narrowing toward both joints, where a smaller ball (`joint`) keeps the bend filled.
 * A tube of constant radius reads as a mannequin; this reads as an upper arm, a calf.
 */
const segment = (a: Vec3, b: Vec3, r: number, m: Material, group: PartGroup, exponent?: number): Ellipsoid => {
    const along = sub(b, a)
    const half = Math.sqrt(dot(along, along)) / 2
    const y = normalize(along)
    const x = normalize(Math.abs(y[2]) < 0.9 ? cross(y, [0, 0, 1]) : cross(y, [1, 0, 0]))
    const z = cross(x, y)
    const basis: Mat3 = [x[0], y[0], z[0], x[1], y[1], z[1], x[2], y[2], z[2]]
    return ellipsoid(scale(add(a, b), 0.5), basis, [r, half + r * 0.45, r], m, group, exponent ? { exponent } : {})
}
/**
 * A foot: an ellipsoid from heel to toe, as tall as `r` and `width` across, so from the front it is
 * the broad wedge LPC draws below the ankle rather than a peg the width of the shin.
 */
const foot = (heel: Vec3, toe: Vec3, r: number, width: number, m: Material, group: PartGroup): Ellipsoid => {
    const along = sub(toe, heel)
    const half = Math.sqrt(dot(along, along)) / 2
    const z = normalize(along)
    const x = normalize(cross([0, 1, 0], z))
    const y = cross(z, x)
    const basis: Mat3 = [x[0], y[0], z[0], x[1], y[1], z[1], x[2], y[2], z[2]]
    // Squared across and underneath: a flat sole, not a rocker.
    return ellipsoid(scale(add(heel, toe), 0.5), basis, [width, r, half + r], m, group, { exponent: [3, 3, 2.2] })
}
const joint = (at: Vec3, r: number, m: Material, group: PartGroup): Ellipsoid =>
    ellipsoid(at, [1, 0, 0, 0, 1, 0, 0, 0, 1], [r, r, r], m, group)

/** Name the limb segment a primitive is (see PrimitiveBase.part). */
const as = <T extends Primitive>(prim: T, part: string): T => ({ ...prim, part })

/** Which arm/leg group a side's limbs belong to. */
const armOf = (side: "L" | "R"): PartGroup => (side === "L" ? "armL" : "armR")
const legOf = (side: "L" | "R"): PartGroup => (side === "L" ? "legL" : "legR")

/** The middle of a closed hand: where a held tool passes through it. */
export const fistCenter = (sk: Skeleton, p: Proportions, side: Side): Vec3 => {
    const j = sk[side]
    return p.handLength
        ? add(j.hand, scale(normalize(sub(j.hand, j.elbow)), p.handLength / 2))
        : add(j.hand, apply(sk.torsoBasis, [-sideSign(side) * p.handInset, 0, 0]))
}

/**
 * Which way a blade runs from the fist. A blade sits lower in the hand than an axe's haft stands:
 * halfway between the grip and the forearm's line, so at rest it points forward-down past the
 * thigh and in a cut it runs on straight from the arm.
 */
export const bladeAxis = (j: LimbJoints): Vec3 => normalize(add(normalize(j.grip), normalize(sub(j.hand, j.elbow))))

/**
 * Which way a sword's blade runs from the fist: along the grip, as the axe's haft does -- carried,
 * up and ahead of the fist, as LPC carries its longsword; in a cut, wherever the wrist turns it.
 */
export const swordAxis = (j: LimbJoints): Vec3 => normalize(j.grip)

/** How far behind the sword hand's fist the other hand takes a two-handed grip. */
export const twoHandSpan = (p: Proportions): number => p.handRadius * 2

/** Body armour Ember draws: a leather jerkin, a legionary's banded cuirass, a plate breastplate. */
export type ArmorKind = "leather" | "legion" | "plate"

/**
 * Paint ellipsoids by where a point of them is on the body and which way the surface faces there
 * (their decals get local points; the normal is the superellipsoid's gradient, as the raster's).
 */
type Paint = (world: Vec3, normal: Vec3) => number | undefined
const painted = (prims: Primitive[], paint: Paint): Primitive[] =>
    prims.map(prim => {
        if (prim.kind !== "ellipsoid") return prim
        const e = typeof prim.exponent === "number" ? [prim.exponent, prim.exponent, prim.exponent] : prim.exponent ?? [2, 2, 2]
        return {
            ...prim,
            decal: (q: Vec3) => {
                const g = q.map((v, i) => (Math.sign(v) * Math.pow(Math.abs(v) / prim.radii[i], e[i] - 1)) / prim.radii[i]) as Vec3
                return paint(add(prim.center, apply(prim.basis, q)), normalize(apply(prim.basis, g)))
            },
        }
    })

/**
 * Metal's own light, for a body turned to `facing`. Polished metal shows what it reflects, not how
 * much light falls on it: where the reflection of the camera's view looks up at the sky it is lit,
 * brightest just above the horizon, then a hard dark band where it starts to show the ground,
 * easing a little lighter below; plus a tight glint toward the lamp. The broad lit side cloth gets
 * reads, on a cuirass, as a t-shirt.
 */
const sheen = (m: Material, facing: Facing) => {
    const turn = rotY(FACING_YAW[facing])
    const light = DEFAULT_RASTER.light
    const view: Vec3 = [0, Math.sin(DEFAULT_RASTER.pitch), Math.cos(DEFAULT_RASTER.pitch)]
    const half = normalize(add(light, view))
    return (normal: Vec3): number => {
        const n = apply(turn, normal)
        if (dot(n, half) > 0.95) return m.light
        // The view reflected off the surface: how far up (sky) or down (ground) it looks.
        const skyward = 2 * dot(n, view) * n[1] - view[1]
        if (dot(n, light) < -0.35) return m.deep
        return skyward > 0.45 ? m.base : skyward > 0.08 ? m.light : skyward > -0.22 ? m.deep : m.shade
    }
}

/** Paint over prims' own decals (theirs still shows wherever `paint` leaves a point alone). */
const overPainted = (prims: Primitive[], paint: (world: Vec3, normal: Vec3) => number | undefined): Primitive[] =>
    prims.map(prim => {
        if (prim.kind !== "ellipsoid") return prim
        const own = prim.decal
        const [over] = painted([prim], paint) as Ellipsoid[]
        return { ...over, decal: (q: Vec3) => over.decal!(q) ?? own?.(q) }
    })

/** Painted light for a body turned to `facing`: a ramp step, 0 lit to 3 deep (decals skip the raster's shading). */
const lighting = (facing: Facing) => {
    const turn = rotY(FACING_YAW[facing])
    const light = DEFAULT_RASTER.light
    return (normal: Vec3): number => {
        const lit = dot(apply(turn, normal), light)
        return lit > 0.6 ? 0 : lit > 0.05 ? 1 : lit > -0.45 ? 2 : 3
    }
}
const tone = (m: Material, step: number): number => [m.light, m.base, m.shade, m.deep][step]

/**
 * Which way a primitive's surface faces at `q`, a point of its own frame (the superellipsoid's
 * gradient, as the raster lights it), in the body's frame: for paint that works in a volume's own
 * frame, as fur does.
 */
const normalOf = (prim: Ellipsoid, q: Vec3): Vec3 => {
    const e = typeof prim.exponent === "number" ? [prim.exponent, prim.exponent, prim.exponent] : prim.exponent ?? [2, 2, 2]
    const g = q.map((v, i) => (Math.sign(v) * Math.pow(Math.abs(v) / prim.radii[i], e[i] - 1)) / prim.radii[i]) as Vec3
    return normalize(apply(prim.basis, g))
}

/** A cell's hash, from three whole numbers: scattered noise (fur strands, curls) that keeps still on the volume. */
const hash3 = (i: number, j: number, k: number): number => {
    let h = Math.imul(i, 374761393) + Math.imul(j, 668265263) + Math.imul(k, 1274126177)
    h = Math.imul(h ^ (h >>> 13), 1274126177)
    return (h ^ (h >>> 16)) >>> 0
}

/**
 * A head of hair as a few numbers, so every one of the game's styles is a record, not a drawing:
 * the parameters of hair.json's `shell` model. How far it stands off the skull, how low the fringe
 * comes, how long it falls, and what it has besides -- sides shaved, something on top, a tie, a texture.
 */
export interface HairDesign {
    /** Stand-off of the cap over the skull: 0.3 a buzz cut, 0.9 a short cut, 3 an afro. */
    volume: number
    /** The front hairline: 0 high off the brow, 0.35 a short cut's, 1 bangs to the eyebrows. */
    fringe: number
    /** Down the back: to the nape, framing the face to the jaw, or falling to the shoulders, mid-back, waist. */
    length: "nape" | "jaw" | "shoulder" | "back" | "waist"
    /** Sides: shaved to the skin (a crest left on top), or faded close. The crown: bald. */
    sides?: "shaved" | "faded"
    crown?: "bald"
    top?: "spikes" | "tufts" | "tuft" | "tall-tuft" | "crest" | "tall-crest" | "flat" | "quiff" | "beehive"
    tie?: "ponytail" | "high-ponytail" | "bun" | "topknot" | "bunches" | "braid"
    texture?: "curls" | "locks" | "rows"
}

/**
 * The game's hair styles (content's hair.json): Ember's own (crop, short, long, ponytail) and each of
 * the game's LPC styles as the nearest design. Kept current as content changes.
 */
export const HAIR_DESIGNS: Record<string, HairDesign> = {}

/** The design for a look's hair style; none for bald (or `none`) and for a style it doesn't know, short. */
export const hairDesignFor = (style: string): HairDesign | undefined => emberHairStyle(style)?.params as unknown as HairDesign | undefined

/** How footwear is made (EmberLook.shoes). */
export type ShoeCut = "shoes" | "boots" | "slippers" | "sandals" | "sabatons"

/** How a pair of leg wear is cut (EmberLook.pants). */
export type PantsCut = "trousers" | "shorts" | "pantaloons" | "skirt" | "legion" | "greaves"

/**
 * What Ember draws, by item type: the content's designs (model.ts), kept current as content changes.
 * A shield or what a hand holds is looked up by its type; garments by the look's models.
 */
export let EMBER_HELD_NAMES: string[] = []
export let EMBER_SHIRT_NAMES: string[] = []
export let EMBER_PANTS_NAMES: string[] = []
export let EMBER_SHOES_NAMES: string[] = []
export let EMBER_ARMOR_NAMES: string[] = []
export let EMBER_SHIELD_NAMES: string[] = []
export let EMBER_BAULDRON_NAMES: string[] = []
/** Held in both hands: a shield goes on the back (generate.ts shieldSlung). */
export let TWO_HANDED: string[] = []
/** Long enough to be carried upright, the butt toward the ground (clips.ts poleCarry). */
export let POLEARMS: string[] = []
onEmberModelsChanged(() => {
    const ids = (slot: EmberSlot) => emberDesigns(slot).map(d => d.id)
    EMBER_HELD_NAMES = ids("held")
    EMBER_SHIRT_NAMES = ids("shirt")
    EMBER_PANTS_NAMES = ids("pants")
    EMBER_SHOES_NAMES = ids("shoes")
    EMBER_ARMOR_NAMES = ids("armor")
    EMBER_SHIELD_NAMES = ids("shield")
    EMBER_BAULDRON_NAMES = ids("bauldron")
    TWO_HANDED = emberDesigns("held").filter(d => d.handling?.twoHanded).map(d => d.id)
    POLEARMS = emberDesigns("held").filter(d => d.handling?.polearm).map(d => d.id)
    for (const k of Object.keys(HAIR_DESIGNS)) delete HAIR_DESIGNS[k]
    for (const style of Object.keys(emberHairStyles())) HAIR_DESIGNS[style] = hairDesignFor(style)!
})

/** A worn item's look: its design's model and parameters, in the item's colour. */
const worn = (slot: EmberSlot, type: string) => {
    const d = emberDesign(slot, type)
    return { ...(d?.params ?? {}), ...(d ? { model: d.model } : {}) }
}
/** The look's shirt for a worn shirt item of `type` in `color`. */
export const wornShirt = (type: string, color: string): EmberLook["shirt"] => ({ dye: color, ...worn("shirt", type) }) as EmberLook["shirt"]
/** The look's shoes for a worn shoes item of `type` in `color`. */
export const wornShoes = (type: string, color: string): EmberLook["shoes"] => ({ dye: color, ...worn("shoes", type) })
/** The look's pants for a worn pants item of `type` in `color`. */
export const wornPants = (type: string, color: string): EmberLook["pants"] => ({ dye: color, ...worn("pants", type) })
/** The look's armour for a worn armor item of `type` in `color`. */
export const wornArmor = (type: string, color: string): EmberLook["armor"] => ({ color, ...worn("armor", type) }) as EmberLook["armor"]
/** The look's bauldron for a worn bauldron item of `type` in `color`. */
export const wornBauldron = (type: string, color: string): EmberLook["bauldron"] => ({ color, ...worn("bauldron", type) })

export const buildPrimitives = (
    sk: Skeleton,
    p: Proportions,
    look: EmberLook,
    facing: Facing = "front",
    skins: Record<string, Material> = SKIN,
): Primitive[] => {
    const skin = skins[look.skin] ?? skins.light
    const footWidth = facing === "left" || facing === "right" ? p.footProfileWidth : p.footWidth

    // ---- body: its model (content's bodies.json) -- flesh, bones, a beast, wounds -----------------
    const prims = runEmberModel("body", look.body ?? DEFAULT_BODY_MODEL, {}, { sk, p, look, facing, skin, footWidth, t: p.clothThickness ?? 1 }) as Primitive[]
    const overBody: OverBody[] = []
    prims.push(...dressed(sk, p, look, facing, skin, footWidth, overBody))
    if (overBody.length) {
        // Paint a model lays over the body's own surfaces (a bauldron's strap across the chest): on
        // every primitive of its groups, skin, shirt or armour alike, once everything is built.
        return prims.map(prim => overBody.reduce((at, o) => (o.groups.includes(at.group) ? (overPainted([at], o.paint as Paint)[0] as Primitive) : at), prim))
    }
    return prims
}

/** What the body has on: clothes, armour, hair, what it holds. */
const dressed = (sk: Skeleton, p: Proportions, look: EmberLook, facing: Facing, skin: Material, footWidth: number, overBody: OverBody[] = []): Primitive[] => {
    const prims: Primitive[] = []

    // ---- garments: inflated copies of what they cover -----------------------------------------
    // How far cloth stands off the skin, scaled for bodies slimmer than LPC's.
    const t = p.clothThickness ?? 1
    // Each slot is drawn by its model (model.ts, content's sprites/ember/equipment.json), in this order: leg
    // wear, footwear, shirt, belt, armour, bauldron, shield, then hair (the body's own) and what the
    // hand holds. A look names its garments' models and their parameters; a shield and what the
    // hand holds are item types, drawn by their designs.
    const context = { sk, p, look, facing, skin, footWidth, t }
    const wear = (slot: EmberModelSlot, model: string, params: object, bind?: Record<string, string>) =>
        prims.push(...(runEmberModel(slot, model, params as Record<string, unknown>, context, overBody, bind) as Primitive[]))
    if (look.pants) wear("pants", look.pants.model ?? look.pants.cut ?? "trousers", look.pants)
    if (look.shoes) wear("shoes", look.shoes.model ?? look.shoes.cut ?? "shoes", look.shoes)
    if (look.shirt) wear("shirt", look.shirt.model ?? "shirt", look.shirt)
    if (look.belt) wear("belt", look.belt.model ?? "belt", look.belt)
    if (look.armor) wear("armor", look.armor.model ?? look.armor.kind, look.armor)
    if (look.bauldron) wear("bauldron", look.bauldron.model ?? "bauldron", look.bauldron)
    const shield = look.shield && emberDesign("shield", look.shield.type)
    if (shield) wear("shield", shield.model, { ...shield.params, ...look.shield }, shield.bind)

    // ---- hair: its style's model (content's hair.json), a shell over the skull cut away where the
    // face shows. A skull and a beast have none.
    const hair = look.hair && !look.bones && !look.beast ? emberHairStyle(look.hair.style) : undefined
    if (look.hair && hair) wear("hair", hair.model, { ...hair.params, color: look.hair.color })

    // ---- held: what the hand holds -----------------------------------------------------------
    const held = look.held ? emberDesign("held", look.held) : undefined
    if (held) wear("held", held.model, { ...held.params, held: look.held, color: look.heldColor }, held.bind)

    return prims
}

/**
 * Only what a look wears and holds -- no body -- in the posed skeleton: an item on its own, for its
 * icon. `skin` stands in for the body's skin wherever a garment paints it (a neckline's opening).
 */
export const equipmentPrimitives = (sk: Skeleton, p: Proportions, look: EmberLook, facing: Facing, skin: Material): Primitive[] =>
    dressed(sk, p, look, facing, skin, facing === "left" || facing === "right" ? p.footProfileWidth : p.footWidth)

/**
 * Primitives scaled by `k` about the origin, never thinner than `floor` pixels where they were
 * thicker: their cuts and paint still see the unscaled shape, so they cut and paint the same.
 */
export const scalePrimitives = (prims: Primitive[], k: number, floor = 0.7): Primitive[] =>
    prims.map(prim => {
        const r = (v: number) => Math.max(v * k, Math.min(v, floor))
        if (prim.kind === "capsule") return { ...prim, a: scale(prim.a, k), b: scale(prim.b, k), r: r(prim.r) }
        const radii: Vec3 = [r(prim.radii[0]), r(prim.radii[1]), r(prim.radii[2])]
        const back = (q: Vec3): Vec3 => [q[0] * (prim.radii[0] / radii[0]), q[1] * (prim.radii[1] / radii[1]), q[2] * (prim.radii[2] / radii[2])]
        return {
            ...prim,
            center: scale(prim.center, k),
            radii,
            ...(prim.keep ? { keep: (q: Vec3) => prim.keep!(back(q)) } : {}),
            ...(prim.decal ? { decal: (q: Vec3) => prim.decal!(back(q)) } : {}),
        }
    })

/** The radius a primitive can reach from its centre, for screen-space culling. */
export const boundingSphere = (prim: Primitive): { center: Vec3; radius: number } => {
    if (prim.kind === "capsule") {
        const center = scale(add(prim.a, prim.b), 0.5)
        const half = Math.hypot(prim.a[0] - prim.b[0], prim.a[1] - prim.b[1], prim.a[2] - prim.b[2]) / 2
        return { center, radius: half + prim.r }
    }
    return { center: prim.center, radius: Math.max(prim.radii[0], prim.radii[1], prim.radii[2]) }
}

/**
 * What a model (model.ts) can use: the body's own volumes and the shapes, light and materials the
 * game's drawings are made of, and plain maths. Nothing else is reachable from a model.
 */
setModelBuiltins({
    // maths
    PI: Math.PI, min: Math.min, max: Math.max, abs: Math.abs, sqrt: Math.sqrt, pow: Math.pow, floor: Math.floor, ceil: Math.ceil,
    round: Math.round, sin: Math.sin, cos: Math.cos, tan: Math.tan, atan2: Math.atan2, hypot: Math.hypot, sign: Math.sign,
    // vectors and frames (a Mat3 is nine numbers, its columns the frame's axes)
    add, sub, scale, dot, cross, normalize, lerp, distance, length: vecLength, apply, transpose, mul, rotX, rotY, rotZ, deg, IDENTITY,
    // lists
    range: (n: number) => Array.from({ length: n }, (_, i) => i),
    values: (o: Record<string, unknown>) => Object.values(o),
    keys: (o: Record<string, unknown>) => Object.keys(o),
    // the rig
    SIDES, sideSign, farSide, legLength, fistCenter, bladeAxis, swordAxis, twoHandSpan,
    // shapes, and the surface of one (its normal at a point of its own frame)
    capsule, ellipsoid, segment, foot, joint, inflate, as, armOf, legOf, normalOf,
    // light and paint, and noise to scatter it by (a texture's cells)
    painted, overPainted, lighting, tone, sheen, hash3,
    // materials (`cloth(name)` is a dye's ramp: garments name their colour `dye`); bone by colour
    cloth: dye, dye, hairDye, rampFrom, metalFrom, DYES, BONES,
})
